'use client';

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import { cn } from '@/utils/cn';
import { useLabsStore, usePack } from '../pack-context';
import { StepNav } from './_components';
import { StepShell } from './step-shell';

/**
 * M3 · Reading — the primer on the vertical's core artifact (finance: the income statement). Styled to
 * the SOURCE vanilla prototype (`data-view="m3a"` + `.m3-math-card` in styles.css), rendered through
 * the Extern finance-sprint design system (fs-* tokens + DM Serif Display / Inter).
 *
 * The primer is STATIC for everyone — no branching, no personalization. LAYOUT (from the HTML
 * prototype, NOT the prior Figma number-circle list):
 *   - A LEFT-aligned header: small uppercase eyebrow, a serif headline, and a muted lead line whose
 *     closing clause is emphasized.
 *   - A white "math card" (`.m3-math-card`: surface, hairline, rounded, generous padding, loose
 *     leading) laid out as a 2-COLUMN grid `[28px 1fr]`: the row's OPERATOR in the left column and
 *     `<strong>Term</strong> : hint` in the right.
 *   - A centered serif pull-quote below the card.
 *
 * THE OPERATORS ARE DATA. The shipped finance screen hardcodes them positionally
 * (`['', '−', '=', '−', '=']`) — that array is not presentation, it is the ARITHMETIC OF AN INCOME
 * STATEMENT, and it is silently wrong for any artifact that isn't one (a funnel's rows don't subtract
 * into each other). So each primer row now carries its own `operator`, and a vertical whose artifact
 * is not arithmetic ships empty ones, which render as nothing.
 *
 * Token mapping (SOURCE intent → fs-* token):
 *   - source eyebrow #6B7280                    → fs-text-muted (uppercase, tracked)
 *   - source body `<em>` ink #3A4252            → fs-text-subtle, emphasized clause italic
 *   - source card #FFFFFF + #E6E4DD             → fs-surface + fs-border, rounded-fs-card
 *   - source "−" operator danger / "=" brand    → fs-danger / fs-brand
 *   - source bold term #0A0E1A / hint #6B7280   → fs-text (medium) / fs-text-muted
 *   - source primary CTA brand-green            → BrandButton primary (fs-primary, the v2 brand)
 */

/** Operator color (− reads as a subtraction / = as a result / blank is neutral) — avoids a nested ternary. */
function operatorClass(op: string): string {
  if (op === '−') {
    return 'text-fs-danger';
  }
  if (op === '=') {
    return 'text-fs-brand';
  }
  return 'text-fs-text-muted';
}
export function M3Reading() {
  const $ = _c(37);
  const {
    copy
  } = usePack();
  const next = useLabsStore(_temp);
  const primer = copy.m3.primer;
  let t0;
  if ($[0] !== primer.rows) {
    t0 = primer.rows.some(_temp2);
    $[0] = primer.rows;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const hasOperators = t0;
  const idx = primer.body.indexOf(": ");
  let t1;
  if ($[2] !== idx || $[3] !== primer.body) {
    t1 = idx === -1 ? [primer.body, ""] : [primer.body.slice(0, idx + 1), primer.body.slice(idx + 2)];
    $[2] = idx;
    $[3] = primer.body;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const [bodyLead, bodyQuestion] = t1;
  let t2;
  if ($[5] !== primer.eyebrow) {
    t2 = <div className="font-medium text-[11px] text-fs-text-muted uppercase tracking-[0.12em]">{primer.eyebrow}</div>;
    $[5] = primer.eyebrow;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== primer.headline) {
    t3 = <h1 className="font-instrument text-fs-display-sm text-fs-text sm:text-fs-display-md">{primer.headline}</h1>;
    $[7] = primer.headline;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== bodyQuestion) {
    t4 = <em className="italic">{bodyQuestion}</em>;
    $[9] = bodyQuestion;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== bodyLead || $[12] !== t4) {
    t5 = <p className="text-fs-body-lg text-fs-text-subtle">{bodyLead} {t4}</p>;
    $[11] = bodyLead;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t2 || $[15] !== t3 || $[16] !== t5) {
    t6 = <header className="flex flex-col gap-2">{t2}{t3}{t5}</header>;
    $[14] = t2;
    $[15] = t3;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const t7 = hasOperators ? "grid-cols-[28px_1fr]" : "grid-cols-1";
  let t8;
  if ($[18] !== t7) {
    t8 = cn("grid gap-x-4 gap-y-4 tabular-nums", t7);
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== hasOperators || $[21] !== primer.rows) {
    let t10;
    if ($[23] !== hasOperators) {
      t10 = row_0 => <div className="contents" key={row_0.label}>{hasOperators ? <span aria-hidden={true} className={operatorClass(row_0.operator)}>{row_0.operator}</span> : null}<span className="text-fs-body-lg text-fs-text-subtle [overflow-wrap:anywhere]"><span className="font-medium text-fs-text">{row_0.label}</span><span className="text-fs-text-muted">: {row_0.hint}</span></span></div>;
      $[23] = hasOperators;
      $[24] = t10;
    } else {
      t10 = $[24];
    }
    t9 = primer.rows.map(t10);
    $[20] = hasOperators;
    $[21] = primer.rows;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[25] !== t8 || $[26] !== t9) {
    t10 = <div className="rounded-fs-card border border-fs-border bg-fs-surface p-6 leading-relaxed sm:p-8"><div className={t8}>{t9}</div></div>;
    $[25] = t8;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== primer.pullQuote) {
    t11 = <p className="text-center font-instrument text-fs-text-subtle text-fs-title-lg">{primer.pullQuote}</p>;
    $[28] = primer.pullQuote;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== next) {
    t12 = <StepNav onNext={next} />;
    $[30] = next;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== t10 || $[33] !== t11 || $[34] !== t12 || $[35] !== t6) {
    t13 = <StepShell><div className="flex w-full flex-col gap-8">{t6}{t10}{t11}{t12}</div></StepShell>;
    $[32] = t10;
    $[33] = t11;
    $[34] = t12;
    $[35] = t6;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  return t13;
}
function _temp2(row) {
  return row.operator !== "";
}
function _temp(s) {
  return s.next;
}